import { useEffect, useMemo, useState } from 'react';
import { scenarios } from '../data/scenarios';
import { defaultConfig, PLACEHOLDER_HOLD_MINUTES } from '../data/program';
import { capacitySummary, resolveOffer } from '../utils/capacity';
import { buildCart } from '../utils/pricing';
import type {
  CustomerStep,
  Device,
  Notice,
  OfferType,
  ProgramConfig,
  Reservation,
  RoleId,
  Scenario,
  SomeoneElseInfo,
  StaffingRole,
  ViewMode,
  VolunteerChoice,
} from '../types/volunteer';

export interface ProtoState {
  view: ViewMode;
  device: Device;
  showAnnotations: boolean;
  config: ProgramConfig;
  unassignedClaims: number;
  otherReservations: number;
  step: CustomerStep;
  choice: VolunteerChoice | null;
  reservation: Reservation;
  notice: Notice;
  pageOffer: OfferType;
  someone: SomeoneElseInfo;
  activeScenario: string;
}

const HOLD_MS = PLACEHOLDER_HOLD_MINUTES * 60 * 1000;
const emptySomeone: SomeoneElseInfo = { firstName: '', lastName: '', email: '', phone: '', relationship: '' };
const noHold: Reservation = { status: 'none', expiresAt: null };

const isEligible = (c: VolunteerChoice | null) => c === 'myself' || c === 'someone';
const ownHold = (s: ProtoState) => (s.reservation.status === 'reserved' ? 1 : 0);
const availableFor = (s: ProtoState) =>
  capacitySummary(s.config, s.unassignedClaims, s.otherReservations + ownHold(s)).available;
const offerFor = (s: ProtoState) => resolveOffer(s.config, availableFor(s), ownHold(s) === 1);
const fallbackOffer = (s: ProtoState): OfferType => (s.config.coachCreditEnabled ? 'coachCredit' : 'none');
const newHold = (): Reservation => ({ status: 'reserved', expiresAt: Date.now() + HOLD_MS });

function applyPreset(scn: Scenario, showAnnotations: boolean): ProtoState {
  const p = scn.preset;
  const s: ProtoState = {
    view: p.view,
    device: p.device ?? 'mobile',
    showAnnotations,
    config: { ...defaultConfig, ...p.config },
    unassignedClaims: p.unassignedClaims ?? 1,
    otherReservations: p.otherReservations ?? 1,
    step: p.step ?? 'volunteer',
    choice: p.choice ?? null,
    reservation: p.hold
      ? { status: p.hold, expiresAt: p.hold === 'reserved' ? Date.now() + HOLD_MS : null }
      : noHold,
    notice: p.notice ?? null,
    pageOffer: 'none',
    someone: { ...emptySomeone, ...p.someone },
    activeScenario: scn.id,
  };
  return { ...s, pageOffer: p.pageOffer ?? offerFor(s) };
}

function expire(s: ProtoState): ProtoState {
  if (s.reservation.status !== 'reserved') return s;
  return { ...s, reservation: { status: 'expired', expiresAt: null }, notice: 'expired' };
}

/** Revalidate an expired hold (simulates the server check when the customer returns). */
function recheck(s: ProtoState): ProtoState {
  if (s.reservation.status !== 'expired') return s;
  if (!isEligible(s.choice)) return { ...s, reservation: noHold, notice: null };
  if (availableFor(s) > 0) return { ...s, reservation: newHold(), notice: 'expired-rereserved', pageOffer: 'discount' };
  return { ...s, reservation: { status: 'released', expiresAt: null }, notice: 'expired-unavailable', pageOffer: fallbackOffer(s) };
}

function choose(prev: ProtoState, c: VolunteerChoice): ProtoState {
  const next = { ...prev, choice: c };
  if (isEligible(c)) {
    if (prev.reservation.status === 'reserved') return { ...next, notice: null };
    if (prev.pageOffer === 'discount') {
      // Server-side revalidation at the moment of selection.
      if (availableFor(prev) > 0) return { ...next, reservation: newHold(), notice: null };
      return { ...next, reservation: noHold, notice: 'unavailable', pageOffer: fallbackOffer(prev) };
    }
    return { ...next, notice: prev.notice === 'unavailable' ? 'unavailable' : null };
  }
  if (prev.reservation.status === 'reserved') {
    return { ...next, reservation: { status: 'released', expiresAt: null }, notice: 'removed' };
  }
  const keep = prev.notice === 'removed' || prev.notice === 'unavailable';
  return { ...next, notice: keep ? prev.notice : null };
}

export function usePrototype() {
  const [state, setState] = useState<ProtoState>(() => applyPreset(scenarios[0], true));
  const [now, setNow] = useState(() => Date.now());
  const holding = state.reservation.status === 'reserved';
  const expiresAt = state.reservation.expiresAt;

  useEffect(() => {
    if (!holding) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [holding]);

  useEffect(() => {
    if (holding && expiresAt !== null && now >= expiresAt) setState((prev) => expire(prev));
  }, [now, holding, expiresAt]);

  const summary = useMemo(
    () => capacitySummary(state.config, state.unassignedClaims, state.otherReservations + (holding ? 1 : 0)),
    [state.config, state.unassignedClaims, state.otherReservations, holding],
  );
  const discountApplied = holding && state.config.discountEnabled && isEligible(state.choice);
  const cart = useMemo(() => buildCart(state.config.amount, discountApplied), [state.config.amount, discountApplied]);
  const holdRemainingMs = holding && expiresAt !== null ? expiresAt - now : 0;

  const actions = useMemo(
    () => ({
      applyScenario: (id: string) =>
        setState((prev) => {
          const scn = scenarios.find((s) => s.id === id);
          return scn ? applyPreset(scn, prev.showAnnotations) : prev;
        }),
      setView: (view: ViewMode) =>
        setState((prev) => (view === 'customer' ? { ...prev, view, pageOffer: offerFor(prev) } : { ...prev, view })),
      setDevice: (device: Device) => setState((prev) => ({ ...prev, device })),
      toggleAnnotations: () => setState((prev) => ({ ...prev, showAnnotations: !prev.showAnnotations })),
      updateConfig: (patch: Partial<ProgramConfig>) =>
        setState((prev) => {
          const s = { ...prev, config: { ...prev.config, ...patch } };
          return prev.view === 'customer' && prev.step === 'volunteer' ? { ...s, pageOffer: offerFor(s) } : s;
        }),
      updateRoleOpportunities: (id: RoleId, opportunities: number) =>
        setState((prev) => ({
          ...prev,
          config: {
            ...prev.config,
            roles: prev.config.roles.map((r) => (r.id === id ? { ...r, opportunities } : r)),
          },
        })),
      updateStaffing: (id: RoleId, patch: Partial<StaffingRole>) =>
        setState((prev) => ({
          ...prev,
          config: {
            ...prev.config,
            staffing: prev.config.staffing.map((r) => (r.id === id ? { ...r, ...patch } : r)),
          },
        })),
      selectChoice: (c: VolunteerChoice) => setState((prev) => choose(prev, c)),
      updateSomeone: (patch: Partial<SomeoneElseInfo>) =>
        setState((prev) => ({ ...prev, someone: { ...prev.someone, ...patch } })),
      goToStep: (step: CustomerStep) =>
        setState((prev) => {
          const s = recheck(prev);
          return step === 'volunteer' ? { ...s, step, pageOffer: offerFor(s) } : { ...s, step };
        }),
      expireHold: () => setState((prev) => expire(prev)),
      recheckHold: () => setState((prev) => recheck(prev)),
      claimRemaining: () =>
        setState((prev) => ({ ...prev, otherReservations: prev.otherReservations + availableFor(prev) })),
      releaseOthers: () => setState((prev) => ({ ...prev, otherReservations: 0 })),
      reloadPage: () =>
        setState((prev) => {
          const s = recheck(prev);
          return { ...s, step: 'volunteer', notice: null, pageOffer: offerFor(s) };
        }),
      checkout: () =>
        setState((prev) => {
          const applies = prev.reservation.status === 'reserved' && prev.config.discountEnabled && isEligible(prev.choice);
          if (!applies) return { ...prev, step: 'confirmed', notice: null };
          return {
            ...prev,
            step: 'confirmed',
            notice: null,
            unassignedClaims: prev.unassignedClaims + 1,
            reservation: { status: 'claimed', expiresAt: null },
          };
        }),
      restartRegistration: () =>
        setState((prev) => {
          const s: ProtoState = { ...prev, step: 'volunteer', choice: null, reservation: noHold, notice: null, someone: emptySomeone };
          return { ...s, pageOffer: offerFor(s) };
        }),
    }),
    [],
  );

  return { state, summary, cart, discountApplied, holdRemainingMs, actions };
}

export type PrototypeApi = ReturnType<typeof usePrototype>;
